import { AlertCircle, Info } from 'lucide-react';
import type { ReactNode } from 'react';

import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTokenLimit } from '@/hooks/useTokenLimit';
import { cn, formatNumber } from '@/lib/utils';

const BASE_QUOTA_TOKENS = 30_000_000;

export function UsagePanel() {
  const { tokenUsage, isLoading } = useTokenLimit();

  if (isLoading || !tokenUsage) {
    return (
      <UsageCard title="Token usage">
        <div className="mt-6 animate-pulse space-y-4">
          <div className="h-4 w-1/4 rounded-sm bg-muted" />
          <div className="h-3 rounded-full bg-muted" />
          <div className="h-3 w-3/4 rounded-sm bg-muted" />
        </div>
      </UsageCard>
    );
  }

  const { current_month_usage, hard_limit, is_within_limit, percentage_used } = tokenUsage;
  const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
  const showBaseQuota = hard_limit > BASE_QUOTA_TOKENS;
  const baseQuotaLeft = `${(BASE_QUOTA_TOKENS / hard_limit) * 100}%`;

  let usedLabel: string;
  if (percentage_used === 0) usedLabel = 'No usage yet';
  else if (percentage_used < 1) usedLabel = '<1% of limit';
  else if (percentage_used < 100) usedLabel = `${percentage_used.toFixed(0)}% of limit`;
  else usedLabel = 'Token limit reached';

  return (
    <div className="space-y-4">
      <UsageCard title={`Token usage - ${currentMonth}`}>
        <div className="mt-6 space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{formatNumber(current_month_usage.total_tokens)} tokens used</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex cursor-help items-center gap-1">
                    {formatNumber(hard_limit)} limit
                    <Info className="h-3 w-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    This is the monthly token limit for this account. Reach out to{' '}
                    <a href="mailto:support@crimson.law" className="underline">
                      support@crimson.law
                    </a>{' '}
                    to request an increase.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className={cn('relative', showBaseQuota && 'mt-8')}>
            <Progress value={Math.min(percentage_used, 100)} className="h-3" />
            {showBaseQuota && (
              <>
                <div
                  className="absolute top-0 h-3 w-px bg-foreground/40"
                  style={{ left: baseQuotaLeft }}
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="absolute -top-6 flex cursor-help items-center gap-1 text-xs text-muted-foreground"
                        style={{ left: baseQuotaLeft, transform: 'translateX(-50%)' }}
                      >
                        <span>Base quota</span>
                        <Info className="h-3 w-3" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>
                        The base subscription includes 30 million tokens per user per month. Overage
                        is charged in 1M token increments.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground">{usedLabel}</p>
        </div>
      </UsageCard>

      {!is_within_limit && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="text-sm">
            <p className="font-medium text-foreground">Token limit exceeded</p>
            <p className="mt-1 text-muted-foreground">
              Document uploads and Case Partner are disabled. Contact{' '}
              <a href="mailto:support@crimson.law" className="underline">
                support@crimson.law
              </a>{' '}
              to increase your limit.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function UsageCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <h2 className="text-sm font-medium text-foreground">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Usage is reset at the start of each month.
      </p>
      {children}
    </div>
  );
}
